import { useEffect, useState } from "react";
import { adminApi, formatApiError } from "@/admin/api";
import PageHeader, { Btn } from "@/admin/components/PageHeader";
import { toast } from "sonner";
import { Plus, Pencil, Trash, Eye } from "@phosphor-icons/react";

const STARTER = `<!DOCTYPE html>
<html><body style="font-family:Arial,sans-serif;max-width:600px;margin:auto;padding:24px;color:#111">
  <h1 style="font-size:24px">Hi {{name}},</h1>
  <p>We're cad4surveys — specialists in land surveying CAD drafting across all 50 US states.</p>
  <p>Would you like a free sample drawing on your next {{city}}, {{state}} project?</p>
  <p><a href="https://example.com" style="background:#0044FF;color:#fff;padding:10px 16px;text-decoration:none">Start a free trial</a></p>
  <p style="font-size:12px;color:#888">Unsubscribe: <a href="{{unsubscribe_url}}">click here</a></p>
</body></html>`;

export default function TemplatesPage() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [previewing, setPreviewing] = useState(null);

  const load = async () => {
    try {
      const { data } = await adminApi.get("/templates");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (t) => {
    try {
      if (t.id) {
        await adminApi.put(`/templates/${t.id}`, {
          name: t.name, subject: t.subject, html_body: t.html_body, preheader: t.preheader,
        });
        toast.success("Template updated");
      } else {
        await adminApi.post("/templates", {
          name: t.name, subject: t.subject, html_body: t.html_body, preheader: t.preheader,
        });
        toast.success("Template created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete template?")) return;
    await adminApi.delete(`/templates/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-templates-page">
      <PageHeader
        eyebrow="Email · Templates"
        title="HTML templates"
        description="Reusable email bodies with {{name}}, {{city}}, {{state}}, {{company}} variables."
        actions={
          <Btn onClick={() => setEditing({ name: "", subject: "", html_body: STARTER })} data-testid="templates-new">
            <Plus size={14} weight="bold" /> New template
          </Btn>
        }
      />
      <div className="p-8">
        <div className="bg-white border border-zinc-200">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-left">
              <tr className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">
                <th className="p-3">Name</th>
                <th className="p-3">Subject</th>
                <th className="p-3">Updated</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {items.map((t) => (
                <tr key={t.id} className="hover:bg-zinc-50">
                  <td className="p-3 font-medium text-zinc-900">{t.name}</td>
                  <td className="p-3 text-zinc-700">{t.subject}</td>
                  <td className="p-3 text-[11px] font-mono text-zinc-500">
                    {new Date(t.updated_at).toLocaleString()}
                  </td>
                  <td className="p-3 text-right">
                    <button onClick={() => setPreviewing(t)} className="p-1 text-zinc-500 hover:text-[#0044FF]" title="Preview"><Eye size={16} /></button>
                    <button onClick={() => setEditing(t)} className="p-1 text-zinc-500 hover:text-zinc-900 ml-1" title="Edit"><Pencil size={16} /></button>
                    <button onClick={() => del(t.id)} className="p-1 text-zinc-400 hover:text-red-600 ml-1" title="Delete"><Trash size={16} /></button>
                  </td>
                </tr>
              ))}
              {!items.length && (
                <tr><td colSpan={4} className="p-10 text-center text-zinc-500 text-sm">No templates yet. Click "New template" to start.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <TemplateEditor
          initial={editing}
          onCancel={() => setEditing(null)}
          onSave={save}
        />
      )}
      {previewing && (
        <PreviewModal tpl={previewing} onClose={() => setPreviewing(null)} />
      )}
    </div>
  );
}

function TemplateEditor({ initial, onCancel, onSave }) {
  const [t, setT] = useState({
    name: initial.name || "",
    subject: initial.subject || "",
    html_body: initial.html_body || "",
    preheader: initial.preheader || "",
    id: initial.id,
  });

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="bg-white border border-zinc-900 max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-5 border-b border-zinc-200 flex items-center justify-between">
          <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-zinc-500">
            {t.id ? "Edit template" : "New template"}
          </div>
          <button onClick={onCancel} className="text-sm text-zinc-500 hover:text-zinc-900">Close ✕</button>
        </div>
        <div className="grid md:grid-cols-2 flex-1 overflow-hidden">
          <div className="p-6 space-y-4 overflow-y-auto border-r border-zinc-200">
            <Field label="Template name" value={t.name} onChange={(v) => setT({ ...t, name: v })} />
            <Field label="Subject" value={t.subject} onChange={(v) => setT({ ...t, subject: v })} hint="Use {{name}} / {{city}} etc." />
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 block mb-1">HTML body</label>
              <textarea
                value={t.html_body}
                onChange={(e) => setT({ ...t, html_body: e.target.value })}
                rows={18}
                className="w-full border border-zinc-300 p-3 text-xs font-mono focus:outline-none focus:border-[#0044FF]"
                data-testid="tpl-html"
              />
            </div>
          </div>
          <div className="p-6 bg-zinc-50 overflow-y-auto">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-3">Live preview</div>
            <div className="bg-white border border-zinc-300 p-4">
              <div className="text-xs text-zinc-500 mb-2">Subject: <span className="text-zinc-900">{renderStr(t.subject)}</span></div>
              <iframe
                title="preview"
                className="w-full h-[500px] border border-zinc-200"
                srcDoc={renderStr(t.html_body)}
              />
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-zinc-200 flex items-center justify-between">
          <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-zinc-500">
            Variables: {"{{name}}  {{city}}  {{state}}  {{company}}  {{email}}"}
          </div>
          <div className="flex gap-2">
            <button onClick={onCancel} className="px-4 py-2 border border-zinc-300 text-sm">Cancel</button>
            <button
              onClick={() => onSave(t)}
              className="px-4 py-2 bg-[#0044FF] text-white text-sm font-medium"
              data-testid="tpl-save"
            >
              Save template
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewModal({ tpl, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-zinc-900">
        <div className="p-4 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500">Preview · {tpl.name}</div>
            <div className="text-sm font-medium">{renderStr(tpl.subject)}</div>
          </div>
          <button onClick={onClose} className="text-sm text-zinc-500 hover:text-zinc-900">Close ✕</button>
        </div>
        <iframe title="preview-modal" className="w-full flex-1" srcDoc={renderStr(tpl.html_body)} />
      </div>
    </div>
  );
}

function Field({ label, value, onChange, hint }) {
  return (
    <div>
      <label className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 block mb-1">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-zinc-300 p-2 text-sm focus:outline-none focus:border-[#0044FF]"
      />
      {hint && <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}

function renderStr(s) {
  const sample = { name: "Sample Surveyor", city: "Austin", state: "TX", company: "Sample Survey Co", email: "sample@example.com", unsubscribe_url: "#" };
  return Object.entries(sample).reduce((acc, [k, v]) => acc.replaceAll(`{{${k}}}`, v), s || "");
}
